/**
 * src/shell/agents/services/AgentArtifactStore.ts
 *
 * Immutable Artifact Versioning, Branching, and Text Diffing Engine.
 */

import { AgentArtifact } from '../types/gstack-agents-types';

export class AgentArtifactStore {
  private static instance: AgentArtifactStore;
  private artifacts: Map<string, AgentArtifact[]> = new Map(); // Key: artifactId, Value: versions array

  private constructor() {}

  public static getInstance(): AgentArtifactStore {
    if (!AgentArtifactStore.instance) {
      AgentArtifactStore.instance = new AgentArtifactStore();
    }
    return AgentArtifactStore.instance;
  }

  public saveArtifact(
    id: string,
    agentId: string,
    userId: string,
    title: string,
    contentType: 'html' | 'markdown' | 'json',
    content: string,
    editedBy: 'agent' | 'user' = 'agent',
    rating?: number
  ): AgentArtifact {
    const history = this.artifacts.get(id) || [];
    const nextVersion = history.length + 1;

    const artifact: AgentArtifact = {
      id,
      agentId,
      userId,
      version: nextVersion,
      title,
      contentType,
      content,
      parentVersion: nextVersion > 1 ? nextVersion - 1 : undefined,
      createdAt: Date.now(),
      editedBy,
      rating,
    };

    history.push(artifact);
    this.artifacts.set(id, history);
    return artifact;
  }

  public getLatest(id: string): AgentArtifact | undefined {
    const history = this.artifacts.get(id);
    if (!history || history.length === 0) return undefined;
    return history[history.length - 1];
  }

  public getHistory(id: string): AgentArtifact[] {
    return this.artifacts.get(id) || [];
  }

  public diff(id: string, versionA: number, versionB: number): string {
    const history = this.artifacts.get(id) || [];
    const a = history.find((v) => v.version === versionA);
    const b = history.find((v) => v.version === versionB);
    if (!a || !b) return 'Version not found';

    const linesA = a.content.split('\n');
    const linesB = b.content.split('\n');
    const diffLines: string[] = [];

    const maxLines = Math.max(linesA.length, linesB.length);
    for (let i = 0; i < maxLines; i++) {
      const lineA = linesA[i];
      const lineB = linesB[i];
      if (lineA === lineB) {
        diffLines.push(`  ${lineA || ''}`);
      } else {
        if (lineA !== undefined) diffLines.push(`- ${lineA}`);
        if (lineB !== undefined) diffLines.push(`+ ${lineB}`);
      }
    }

    return diffLines.join('\n');
  }
}

export const agentArtifactStore = AgentArtifactStore.getInstance();
